/**
 * Period Formatting Utilities
 * Pure functions for period-aware date formatting and chart configuration
 *
 * Provides utilities for:
 * - Dynamic date formatting based on report period
 * - Chart title generation with period context
 * - X-axis label configuration
 *
 * All functions are pure (no side effects) and type-safe.
 *
 * @module lib/reports/period-utils
 */

import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ReportPeriod } from '@/types/reports';
import { PERIOD_CONFIGS } from './period-config';

/**
 * Format a date according to the selected period
 *
 * Automatically applies the appropriate date format based on the report period:
 * - Daily: "15/10/2025"
 * - Weekly: "Sem 15/10"
 * - Monthly: "out 2025"
 * - Quarterly: "Q4 2025"
 * - Yearly: "2025"
 *
 * @param date - Date string (ISO format) or Date object
 * @param period - Report period type (defaults to monthly)
 * @returns Formatted date string in Portuguese
 *
 * @example
 * ```typescript
 * formatDateByPeriod('2025-10-15', ReportPeriod.DAILY)
 * // Returns: "15/10/2025"
 *
 * formatDateByPeriod('2025-10-15', ReportPeriod.MONTHLY)
 * // Returns: "out 2025"
 * ```
 */
export function formatDateByPeriod(
  date: string | Date,
  period: ReportPeriod = ReportPeriod.MONTHLY
): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const formatString = PERIOD_CONFIGS[period].dateFormat;

  return format(dateObj, formatString, { locale: ptBR });
}

/**
 * Get chart title with period prefix
 *
 * Generates dynamic chart titles that reflect the selected period:
 * - "Evolução Diária [suffix]"
 * - "Evolução Mensal [suffix]"
 * - "Evolução Anual [suffix]"
 *
 * @param period - Report period type (defaults to monthly)
 * @param suffix - Optional suffix to append (e.g., "por Categoria", "de Caixa")
 * @returns Complete chart title with period context
 *
 * @example
 * ```typescript
 * getChartTitle(ReportPeriod.DAILY, 'por Categoria')
 * // Returns: "Evolução Diária por Categoria"
 *
 * getChartTitle(ReportPeriod.MONTHLY)
 * // Returns: "Evolução Mensal"
 * ```
 */
export function getChartTitle(
  period: ReportPeriod = ReportPeriod.MONTHLY,
  suffix: string = ''
): string {
  const { displayName } = PERIOD_CONFIGS[period];
  const base = `Evolução ${displayName}`;
  return suffix ? `${base} ${suffix}` : base;
}

/**
 * Get X-axis label for period
 *
 * Provides the appropriate X-axis label for chart rendering based on period:
 * - Daily: "Dia"
 * - Weekly: "Semana"
 * - Monthly: "Mês"
 * - Quarterly: "Trimestre"
 * - Yearly: "Ano"
 *
 * @param period - Report period type (defaults to monthly)
 * @returns X-axis label in Portuguese
 *
 * @example
 * ```typescript
 * getXAxisLabel(ReportPeriod.DAILY)
 * // Returns: "Dia"
 *
 * getXAxisLabel(ReportPeriod.YEARLY)
 * // Returns: "Ano"
 * ```
 */
export function getXAxisLabel(period: ReportPeriod = ReportPeriod.MONTHLY): string {
  return PERIOD_CONFIGS[period].xAxisLabel;
}
